import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import {
  handleClearFields,
  getPaymentMethod,
  createPaymentMethod,
  updatePaymentMethod,
  updatePaymentMethodConfig,
  getPaymentMethodConfig,
  handleSelectPaymentMethodConfigScope,
} from "store/payments/methods/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ModaPaymentProvidersTabs } from "configs/types";

import Button from "components/buttons/Button";
import MethodsMainTab from "../../tabs/psp/methods/MethodsMainTab";
import MethodsConfigTab, {
  RESERVED_CUSTOM_CONFIG_ERROR_FIELD,
  hasReservedCustomConfigError,
} from "../../tabs/psp/methods/MethodsConfigTab";

import { Modal } from "components/Modal/Modal";
import styles from "./PaymentMethodsEditModal.module.scss";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import { getCurrencyList, getPayoutCurrencyList } from "store/settings/slice";
import { unionPaymentConfigurationCurrencies } from "helpers/paymentConfigurationCurrencies";

const PaymentMethodsEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isItemFetched = useAppSelector(
    (store) => store["payment-methods"].isItemFetched,
  );
  const payload = useAppSelector((store) => store["payment-methods"].payload);
  const config = useAppSelector((store) => store["payment-methods"].config);
  const isItemFetching = useAppSelector(
    (store) => store["payment-methods"].isItemFetching,
  );

  const geoList = useAppSelector(selectFilteredGeo);
  const baseCurrencies = useAppSelector((store) => store.settings.currencies);
  const payoutCurrencies = useAppSelector(
    (store) => store.settings.payoutCurrencies,
  );
  const currenciesList = useMemo(
    () =>
      unionPaymentConfigurationCurrencies(baseCurrencies, payoutCurrencies),
    [baseCurrencies, payoutCurrencies],
  );
  const tabs = [
    ModaPaymentProvidersTabs.main,
    ModaPaymentProvidersTabs.config,
  ];
  const [activeTab, setActiveTab] = useState<ModaPaymentProvidersTabs>(
    ModaPaymentProvidersTabs.main,
  );
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [isConfigFetched, setIsConfigFetched] = useState(false);
  const [configReloadRevision, setConfigReloadRevision] = useState(0);
  const configLoadSequence = useRef(0);
  const loadedConfigScope = useRef<string | null>(null);
  const formErrorScope = useRef<string | null>(null);

  const formErrors = useFormErrors();
  const entityId = searchParams.get("entityId");
  const isEdit = Boolean(entityId);
  const [isCreateFlow] = useState(!isEdit);
  const permissions = usePermissionHelper();
  const canUpdate = permissions?.update ?? false;
  const canCreate = permissions?.create ?? false;
  const canEdit = isCreateFlow ? canCreate : canUpdate;

  useEffect(() => {
    dispatch(getCurrencyList());
    dispatch(getPayoutCurrencyList());
  }, [dispatch]);

  useEffect(() => {
    if (entityId && !isItemFetched) {
      dispatch(getPaymentMethod(entityId));
    }
  }, [entityId, isItemFetched]);

  const isScopedConfigTab = activeTab === ModaPaymentProvidersTabs.config;

  useEffect(() => {
    if (
      isScopedConfigTab &&
      (!config?.country || !config?.currency) &&
      geoList[0] &&
      currenciesList[0]
    ) {
      dispatch(
        handleSelectPaymentMethodConfigScope({
          country: geoList[0],
          currency: currenciesList[0],
        }),
      );
    }
  }, [
    config?.country,
    config?.currency,
    currenciesList,
    dispatch,
    geoList,
    isScopedConfigTab,
  ]);

  useEffect(() => {
    if (
      !isScopedConfigTab ||
      !entityId ||
      !config?.country ||
      !config?.currency
    ) {
      return;
    }
    const scope = `${entityId}|${config.country}|${config.currency}|${configReloadRevision}`;
    const errorScope = `${entityId}|${config.country}|${config.currency}`;
    if (formErrorScope.current !== errorScope) {
      formErrorScope.current = errorScope;
      formErrors.resetErrors();
    }
    if (loadedConfigScope.current === scope) {
      setIsConfigFetched(true);
      return;
    }

    const sequence = ++configLoadSequence.current;
    setIsConfigFetched(false);
    setIsConfigLoading(true);
    dispatch(
      getPaymentMethodConfig({
        id: entityId,
        country: config.country,
        currency: config.currency,
      }),
    )
      .unwrap()
      .then(() => {
        if (sequence === configLoadSequence.current) {
          loadedConfigScope.current = scope;
          setIsConfigFetched(true);
        }
      })
      .catch((error) => {
        if (sequence === configLoadSequence.current) {
          setIsConfigFetched(false);
          handleApiError({
            error,
            t,
            dispatch,
            formErrors: undefined,
          });
        }
      })
      .finally(() => {
        if (sequence === configLoadSequence.current) {
          setIsConfigLoading(false);
        }
      });
  }, [
    activeTab,
    config?.country,
    config?.currency,
    configReloadRevision,
    dispatch,
    entityId,
    isScopedConfigTab,
    t,
  ]);

  const isLoaded = !isEdit || isItemFetched;
  const selectedConfigScope =
    entityId && config?.country && config?.currency
      ? `${entityId}|${config.country}|${config.currency}|${configReloadRevision}`
      : null;
  const isSelectedConfigFetched =
    isConfigFetched && loadedConfigScope.current === selectedConfigScope;

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

const handleApply = async () => {
  if (!canEdit) return;
  if (
    activeTab === ModaPaymentProvidersTabs.config &&
    hasReservedCustomConfigError(formErrors.errors)
  ) {
    return;
  }
  if (payload && activeTab === ModaPaymentProvidersTabs.main) {
    if (!isEdit) {
      formErrors.resetErrors();
    }

    try {
      if (isEdit) {
        await dispatch(updatePaymentMethod(payload)).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } else {
        const result = await dispatch(createPaymentMethod(payload)).unwrap(); 

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );

        navigate(
          `${location.pathname}?activeModal=paymentMethod&entityId=${result}`,
        );
      }
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
      return;
    }
  }

  if (config && activeTab === ModaPaymentProvidersTabs.config) {
    if (
      Number(config.toCompletedDepositCount) > 0 &&
      Number(config.fromCompletedDepositCount) >=
        Number(config.toCompletedDepositCount)
    ) {
      formErrors.setFieldError("toCompletedDepositCount", "InvalidValue");

      dispatch(
        showNotification({
          title: t("warning"),
          message: t("maxCompletedDepositsRangeError"),
          type: NotificationTypes.warning,
        }),
      );

      return;
    }
    
    try {
      await dispatch(
        updatePaymentMethodConfig({
          ...config,
          id: entityId!,
        }),
      ).unwrap();

      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityUpdateSuccess"),
          type: NotificationTypes.success,
        }),
      );
      setConfigReloadRevision((revision) => revision + 1);
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  }
};

  const handleTabClick = (tab: ModaPaymentProvidersTabs) => {
    if (!isEdit && tab !== ModaPaymentProvidersTabs.main) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );
      return;
    }
    if (tab !== ModaPaymentProvidersTabs.config) {
      formErrors.clearFieldError(RESERVED_CUSTOM_CONFIG_ERROR_FIELD);
    }
    setActiveTab(tab);
  };

  const renderTab = () => {
    if (activeTab === ModaPaymentProvidersTabs.main) {
      return (
        <MethodsMainTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          isReadOnly={!canEdit}
        />
      );
    }
    if (activeTab === ModaPaymentProvidersTabs.config) {
      return (
        <MethodsConfigTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          setFieldError={formErrors.setFieldError}
          isFetched={isSelectedConfigFetched}
          isReadOnly={!canEdit}
          showAutoWithdrawal={payload?.transactionType === "withdrawal"}
        />
      );
    }
    return null;
  };

return (
  <Modal onClose={handleCloseClick}>
    <Modal.Header>{t("paymentMethod")}</Modal.Header>

    {!isLoaded ? (
      <ModalSkeleton />
    ) : (
      <>
        <Modal.Tabs
          tabs={tabs}
          activeTab={activeTab}
          onClick={handleTabClick}
        />

        <Modal.Content
          className={`${styles.paymentMethodsEditModalContentWrapper} ${
            isScopedConfigTab
              ? styles.configTabContent
              : ""
          }`}
        >
          <div className="bonus-content payment-content">{renderTab()}</div>
        </Modal.Content>

        {canEdit && <Modal.Footer>
          {isScopedConfigTab && isConfigLoading ? (
            <ModalFooterSkeleton />
          ) : (
            <>
              <Button
                isSecondary
                txt={t("cancel")}
                handleClick={handleCloseClick}
              />

              <Button
                isPrimary
                handleClick={handleApply}
                txt={t("save")}
                isLoad={isItemFetching}
                disabled={
                  isScopedConfigTab &&
                  (!isSelectedConfigFetched ||
                    hasReservedCustomConfigError(formErrors.errors))
                }
              />
            </>
          )}
        </Modal.Footer>}
      </>
    )}
  </Modal>
);
};

export default PaymentMethodsEditModal;
